import type { NextPage, GetStaticProps, GetStaticPaths } from 'next';

import PageService from 'services/PageService/PageService';
import PressPageService from 'services/PressPageService/PressPageService';
import DateService from 'services/DateService/DateService';
import { IGenericPage, IPressDetailPage } from 'types/interfaces';
import GlobalLayout from 'blocks/Layouts/GlobalLayout';
import {
  navigationPaths,
  MAX_PRERENDERED_LIST_ITEMS,
  PAGE_INVALIDATION_INTERVAL,
} from 'data/constants';
import HeadService from 'services/HeadService/HeadService';

interface IPressDetail extends IGenericPage, IPressDetailPage {}

const PressDetail: NextPage<IPressDetail> = ({
  globalLayoutProps,
  pressElement: { newsMediaTitle, title, date, content, pdfMedia },
}) => {
  if (pdfMedia && pdfMedia.length > 0) {
    return (
      <GlobalLayout {...globalLayoutProps}>
        <div className="scanned-media-page">
          {pdfMedia && (
            <div>
              <div className="scanned-media-page__container">
                <div className="article-title">
                  <div className="title">{title}</div>
                  <div className="date">
                    {DateService.getFormattedDateString(date)}
                  </div>
                </div>
                {pdfMedia.map(item => (
                  <div className="article-pdf" key={item}>
                    <iframe
                      className="article-pdf__frame"
                      title={title}
                      frameBorder="0"
                      src={`https://docs.google.com/gview?url=${item}&embedded=true`}
                    />
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </GlobalLayout>
    );
  }

  return (
    <GlobalLayout {...globalLayoutProps}>
      <div className="press-detail">
        {newsMediaTitle && (
          <div className="press-detail__news-media-title">{newsMediaTitle}</div>
        )}
        <div className="press-detail__title">{title}</div>
        <div className="press-detail__date">
          {DateService.getFormattedDateString(date)}
        </div>
        <div className="press-detail__content-container">
          <div className="content-box">{content}</div>
        </div>
      </div>
    </GlobalLayout>
  );
};

export default PressDetail;

export const getStaticProps: GetStaticProps = async context => {
  const { params } = context as { params: { id: string } };

  const genericPageProps = await PageService.getGenericPageProps({
    navPath: navigationPaths.PRESS_DETAIL,
  });
  const pressDetailProps = await PageService.getPressDetailPageProps(params.id);

  if (pressDetailProps.pressElement === null) {
    return {
      notFound: true,
    };
  }

  const pressDetailMetadata = HeadService.getMetadataForArticles(
    pressDetailProps.pressElement
  );
  genericPageProps.globalLayoutProps.metaData = pressDetailMetadata;

  return {
    props: {
      ...genericPageProps,
      ...pressDetailProps,
    },
    revalidate: PAGE_INVALIDATION_INTERVAL,
  };
};

export const getStaticPaths: GetStaticPaths = async () => {
  const pressList = await PressPageService.getPressList();
  const paths = pressList
    .slice(0, MAX_PRERENDERED_LIST_ITEMS)
    .map(({ id }) => ({
      params: {
        id: `${id}`,
      },
    }));
  return {
    paths,
    fallback: 'blocking', // Pages, other than the ones prerendered for the returned paths, will be rendered on requests coming from the browser
  };
};
